/**
 * Theme toggle button (BUILD_SPEC.md §15.3).
 *
 * Sun/moon icon, tooltip-labeled in the active locale. Used in both the
 * login page and the in-app header so the affordance is consistent
 * before and after sign-in.
 */

import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useThemeStore } from "@/stores/themeStore";

interface Props {
  /** Compact variant for the header; bigger for the login page. */
  size?: "sm" | "md";
}

export function ThemeToggle({ size = "md" }: Props) {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const toggle = useThemeStore((s) => s.toggle);

  const label = theme === "dark" ? t("common.themeLight") : t("common.themeDark");
  const Icon = theme === "dark" ? Sun : Moon;
  const dim = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="rounded-md p-2 text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40 transition-colors"
    >
      <Icon className={dim} aria-hidden />
    </button>
  );
}
